import { storagePublicOrigin } from '@aimfora/config'
import { isNotFound, isRedirect } from '@tanstack/react-router'
import { createCsrfMiddleware, createMiddleware, createStart } from '@tanstack/react-start'
import { setResponseHeader } from '@tanstack/react-start/server'
import { getWebEnv } from '~/server/env.server'
import { getLogger } from '~/server/logger.server'
import { captureServerException } from '~/server/posthog.server'
import { buildSecurityHeaders, createNonce } from '~/server/security-headers.server'
import { captureServerError } from '~/server/sentry.server'

/**
 * Security headers for every response, with a fresh CSP nonce per request. The nonce goes into
 * the request context so the router can stamp it on the scripts it renders.
 */
const securityMiddleware = createMiddleware().server(async ({ next }) => {
  const env = getWebEnv()
  const nonce = createNonce()
  const headers = buildSecurityHeaders({
    nonce,
    appEnv: env.APP_ENV,
    appUrl: env.APP_URL,
    sentryDsn: env.SENTRY_DSN,
    storageOrigin: storagePublicOrigin(env),
    posthogHost: process.env.VITE_POSTHOG_HOST,
  })
  for (const [name, value] of Object.entries(headers)) setResponseHeader(name, value)
  return next({ context: { nonce } })
})

/** Server functions are same-origin RPC; behind a proxy the public origin is APP_URL. */
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === 'serverFn',
  // Read lazily and only on the server: this file is also part of the client bundle
  origin: (origin) => origin === new URL(process.env.APP_URL ?? 'http://localhost:3000').origin,
})

/** Business-rule failures (DomainError) are answers to the user, not bugs. */
const isExpectedFailure = (error: unknown) => error instanceof Error && error.name === 'DomainError'

/**
 * Report unexpected server function failures; redirects and 404s are control flow. The browser
 * gets a bare code instead of the error itself: the message of a failed query holds SQL and the
 * values that were being written. Development keeps the original, to debug with.
 */
const errorReportingMiddleware = createMiddleware({ type: 'function' }).server(async ({ next }) => {
  try {
    return await next()
  } catch (error) {
    if (isRedirect(error) || isNotFound(error) || isExpectedFailure(error)) throw error
    captureServerError(error)
    await captureServerException(error)
    getLogger().error({ err: error }, 'server function failed')
    if (getWebEnv().APP_ENV === 'development') throw error
    throw new Error('internal_error')
  }
})

export const startInstance = createStart(() => ({
  requestMiddleware: [securityMiddleware, csrfMiddleware],
  functionMiddleware: [errorReportingMiddleware],
}))
